import { Letter, Position } from '../types/game';
import { validateWord } from './wordValidator';

export interface WordPath {
  word: string;
  path: Position[];
}

export const findAllWords = (grid: Letter[][]): WordPath[] => {
  const words: WordPath[] = [];
  const foundWords = new Set<string>(); // Track unique words found
  const gridSize = grid.length;
  
  // Helper function to check if a position is valid and not used
  const isValidPosition = (pos: Position, usedPositions: Set<string>): boolean => {
    const { row, col } = pos;
    if (row < 0 || row >= gridSize || col < 0 || col >= gridSize) {
      return false;
    }
    // Check if position is already used in the current path
    if (usedPositions.has(`${row},${col}`)) {
      return false;
    }
    // Check if the letter is marked as used in the game state
    if (grid[row][col].used) {
      return false;
    }
    return true;
  };
  
  // Helper function to get adjacent positions
  const getAdjacentPositions = (pos: Position): Position[] => {
    const { row, col } = pos;
    const adjacent: Position[] = [];
    
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        adjacent.push({ row: row + dr, col: col + dc });
      }
    }
    
    return adjacent;
  };
  
  // Recursive function to find all words starting from a position
  const findWordsFromPosition = (
    startPos: Position,
    currentPath: Position[],
    usedPositions: Set<string>,
    currentWord: string
  ) => {
    const { row, col } = startPos;
    const letter = grid[row][col];
    const newWord = currentWord + letter.char;
    const newUsedPositions = new Set(usedPositions);
    newUsedPositions.add(`${row},${col}`);
    
    // If the current word is valid and long enough, add it (only if not already found)
    if (newWord.length >= 3 && validateWord(newWord) && !foundWords.has(newWord)) {
      foundWords.add(newWord);
      words.push({
        word: newWord,
        path: [...currentPath, startPos]
      });
    }
    
    // Continue searching if we haven't used all letters
    if (newWord.length < 8) { // Reasonable limit to prevent infinite recursion
      const adjacentPositions = getAdjacentPositions(startPos);
      
      for (const adjPos of adjacentPositions) {
        if (isValidPosition(adjPos, newUsedPositions)) {
          findWordsFromPosition(
            adjPos,
            [...currentPath, startPos],
            newUsedPositions,
            newWord
          );
        }
      }
    }
  };
  
  // Start the search from every unused position in the grid
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      // Only start from letters that are not already used
      if (!grid[row][col].used) {
        findWordsFromPosition(
          { row, col },
          [],
          new Set(),
          ''
        );
      }
    }
  }
  
  // Sort by length (longest first) - no need to remove duplicates since we prevent them
  return words.sort((a, b) => b.word.length - a.word.length);
};

export const getWordsByLength = (words: WordPath[]): { [length: number]: WordPath[] } => {
  const wordsByLength: { [length: number]: WordPath[] } = {};
  
  words.forEach(wordPath => {
    const length = wordPath.word.length;
    if (!wordsByLength[length]) {
      wordsByLength[length] = [];
    }
    wordsByLength[length].push(wordPath);
  });
  
  return wordsByLength;
};

/**
 * Fast version: Find just the first N valid words (or all if less than N)
 * Stops early once we have enough words, making it much faster for hints
 */
export const findFirstWords = (grid: Letter[][], maxWords: number = 1, foundWords: Set<string> = new Set()): WordPath[] => {
  const words: WordPath[] = [];
  const foundWordsSet = new Set<string>(foundWords);
  const gridSize = grid.length;
  
  // Helper function to check if a position is valid and not used
  const isValidPosition = (pos: Position, usedPositions: Set<string>): boolean => {
    const { row, col } = pos;
    if (row < 0 || row >= gridSize || col < 0 || col >= gridSize) {
      return false;
    }
    if (usedPositions.has(`${row},${col}`)) {
      return false;
    }
    if (grid[row][col].used) {
      return false;
    }
    return true;
  };
  
  // Helper function to get adjacent positions
  const getAdjacentPositions = (pos: Position): Position[] => {
    const { row, col } = pos;
    const adjacent: Position[] = [];
    
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        adjacent.push({ row: row + dr, col: col + dc });
      }
    }
    
    return adjacent;
  };
  
  // Recursive function to find words starting from a position
  const findWordsFromPosition = (
    startPos: Position,
    currentPath: Position[],
    usedPositions: Set<string>,
    currentWord: string
  ): boolean => {
    // Early exit: if we have enough words, stop searching
    if (words.length >= maxWords) {
      return true; // Signal to stop
    }
    
    const { row, col } = startPos;
    const letter = grid[row][col];
    const newWord = currentWord + letter.char;
    const newUsedPositions = new Set(usedPositions);
    newUsedPositions.add(`${row},${col}`);
    
    // If the current word is valid and long enough, add it (only if not already found)
    if (newWord.length >= 3 && validateWord(newWord) && !foundWordsSet.has(newWord) && !words.some(w => w.word === newWord)) {
      foundWordsSet.add(newWord);
      words.push({
        word: newWord,
        path: [...currentPath, startPos]
      });
      
      // Early exit: if we have enough words, stop
      if (words.length >= maxWords) {
        return true;
      }
    }
    
    // Continue searching if we haven't used all letters
    if (newWord.length < 8) {
      const adjacentPositions = getAdjacentPositions(startPos);
      
      for (const adjPos of adjacentPositions) {
        if (isValidPosition(adjPos, newUsedPositions)) {
          const shouldStop = findWordsFromPosition(
            adjPos,
            [...currentPath, startPos],
            newUsedPositions,
            newWord
          );
          if (shouldStop) return true; // Propagate stop signal
        }
      }
    }
    
    return false;
  };
  
  // Start the search from every unused position in the grid
  // Stop early once we have enough words
  for (let row = 0; row < gridSize && words.length < maxWords; row++) {
    for (let col = 0; col < gridSize && words.length < maxWords; col++) {
      if (!grid[row][col].used) {
        const shouldStop = findWordsFromPosition(
          { row, col },
          [],
          new Set(),
          ''
        );
        if (shouldStop) break;
      }
    }
    if (words.length >= maxWords) break;
  }
  
  // Sort by length (shortest first) for hints - we want shorter words to be easier
  return words.sort((a, b) => a.word.length - b.word.length);
};
